import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  Skeleton,
  badgeToneForStatus,
} from "@evolua/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { getApiClient } from "../auth/http.js";
import {
  getDemoAdmissionInvite,
  submitDemoAdmissionInviteItem,
} from "../api/demo-admissions.js";
import { useSession } from "../session/SessionContext.js";

/** Portal público do candidato via token (FASE-06-T53). */
export function AdmissaoCandidatoPage() {
  const { token } = useParams<{ token: string }>();
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["admission-invite", token],
    enabled: Boolean(token),
    queryFn: () =>
      isDemoMode
        ? getDemoAdmissionInvite(token!)
        : getApiClient().getAdmissionInvite(token!),
  });

  const submit = useMutation({
    mutationFn: (input: {
      key: string;
      value?: string;
      filename?: string;
      content?: string;
      contentType?: string;
    }) =>
      isDemoMode
        ? Promise.resolve(submitDemoAdmissionInviteItem(token!, input.key, input))
        : getApiClient().submitAdmissionInviteItem(token!, input.key, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admission-invite", token] });
    },
  });

  const correlationId =
    query.error instanceof EvoluaApiError ? query.error.problem.traceId : undefined;

  async function onSubmitField(key: string) {
    setActionError(null);
    try {
      await submit.mutateAsync({ key, value: fieldDrafts[key] ?? "" });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha no envio");
    }
  }

  async function onSubmitDoc(key: string) {
    setActionError(null);
    try {
      await submit.mutateAsync({
        key,
        filename: `${key}.txt`,
        content: `Documento mock enviado em ${new Date().toISOString()}`,
        contentType: "text/plain",
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha no envio");
    }
  }

  if (!token) {
    return <EmptyState title="Convite inválido" description="Token ausente na URL." />;
  }

  if (query.isPending) return <Skeleton height="8rem" label="Admissão" />;

  if (query.isError || !query.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const data = query.data;

  return (
    <div className="evolua-public">
      <main className="evolua-public__panel">
        <PageHeader
          title={`Admissão — ${data.candidateName}`}
          lead={
            <>
              Envie os itens pendentes. Status:{" "}
              <Badge tone={badgeToneForStatus(data.status)}>{data.status}</Badge>
            </>
          }
        />

        {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

        <ul className="evolua-plain-list">
          {data.checklist.map((item) => (
            <li key={item.key} className="evolua-mt">
              <strong>{item.label}</strong>{" "}
              {item.required ? <Badge tone="warning">obrigatório</Badge> : null}{" "}
              <Badge tone={badgeToneForStatus(item.status)}>{item.status}</Badge>
              {item.status === "PENDING" || item.status === "REJECTED" ? (
                <div className="evolua-mt">
                  {item.kind === "FIELD" || item.kind === "TASK" ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        void onSubmitField(item.key);
                      }}
                    >
                      <Field
                        label={item.label}
                        type="text"
                        value={fieldDrafts[item.key] ?? ""}
                        onChange={(e) =>
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [item.key]: e.target.value,
                          }))
                        }
                        aria-label={item.label}
                      />
                      <Button type="submit" size="sm" disabled={submit.isPending}>
                        Enviar
                      </Button>
                    </form>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      disabled={submit.isPending}
                      onClick={() => void onSubmitDoc(item.key)}
                    >
                      Enviar documento mock
                    </Button>
                  )}
                </div>
              ) : null}
              {item.comment ? <p className="evolua-muted">RH: {item.comment}</p> : null}
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
